import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { MessageCircle, Send, CheckCircle, AlertCircle, Save, Link as LinkIcon, KeyRound } from "lucide-react";

/**
 * Per-tenant WhatsApp settings.
 *
 * Two modes:
 *   • stub  — no API; the salon shares wa.me links manually.
 *   • meta  — Meta WhatsApp Cloud API with the salon's own credentials.
 *
 * Lives under the Settings menu now (was previously a top-level page + a
 * platform-admin page). One source of truth per tenant.
 */
export default function WhatsAppSettings() {
  const { lang } = useLanguage();
  const t = (en, ar) => (lang === "ar" ? ar : en);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  // Local form state — meta_access_token is write-only.
  const [form, setForm] = useState({
    mode: "stub",
    meta_phone_number_id: "",
    meta_access_token: "",
    meta_api_version: "v18.0",
    enabled: true,
    test_phone: "",
  });
  const [testBody, setTestBody] = useState(t("Test message from iSaloon", "رسالة اختبار من iSaloon"));

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/whatsapp/settings");
      setSettings(r.data);
      setForm({
        mode: r.data.mode || "stub",
        meta_phone_number_id: r.data.meta_phone_number_id || "",
        meta_access_token: "",
        meta_api_version: r.data.meta_api_version || "v18.0",
        enabled: r.data.enabled !== false,
        test_phone: r.data.test_phone || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Empty token means "don't change" on backend — preserves the stored value.
      const r = await api.put("/whatsapp/settings", form);
      toast.success(t("Saved", "تم الحفظ"));
      setSettings(r.data.settings);
      setForm((f) => ({ ...f, meta_access_token: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    const to = form.test_phone || settings?.test_phone || "";
    if (!to) return toast.error(t("Enter a test phone number", "أدخل رقم جوال للاختبار"));
    setSending(true);
    try {
      const r = await api.post("/whatsapp/send-test", { to, body: testBody });
      if (r.data.success) {
        if (r.data.mode === "stub") {
          toast.message(t("Stub mode — message was logged only", "وضع تجريبي — تم تسجيل الرسالة دون إرسال فعلي"), { duration: 4500 });
        } else {
          toast.success(t("Sent via Meta API ✓", "تم الإرسال عبر Meta API ✓"));
        }
      } else {
        toast.error(r.data.error || t("Send failed", "فشل الإرسال"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  if (loading) return <div className="p-8 text-[var(--text-muted)]">…</div>;

  const isMeta = form.mode === "meta";
  const metaConfigured = isMeta && form.meta_phone_number_id && (settings?.meta_access_token_set || form.meta_access_token);

  return (
    <div className="space-y-6" data-testid="whatsapp-settings">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <MessageCircle size={22} className="text-green-600" />
        </div>
        <div>
          <h2 className="font-heading text-2xl gold-text">
            {t("WhatsApp Settings", "إعدادات واتساب")}
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            {t(
              "Per-tenant WhatsApp configuration. Choose between manual wa.me sharing or the Meta Cloud API.",
              "إعدادات واتساب خاصة بصالونك. اختر بين المشاركة اليدوية عبر wa.me أو واجهة Meta Cloud API.",
            )}
          </p>
        </div>
      </div>

      {/* Mode picker — two distinct cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => setForm({ ...form, mode: "stub" })}
          data-testid="wa-mode-stub"
          className={`text-start p-5 rounded-xl border transition ${
            form.mode === "stub" ? "border-[var(--gold)] bg-[var(--gold)]/10 ring-2 ring-[var(--gold)]/30" : "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--gold)]"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <LinkIcon size={18} className="text-[var(--gold)]" />
            <span className="font-heading text-lg">
              {t("Standard (Manual wa.me)", "العادي (مشاركة يدوية wa.me)")}
            </span>
          </div>
          <p className="text-xs text-[var(--text-muted)] leading-relaxed">
            {t(
              "No API setup. Your team sends WhatsApp messages manually via wa.me links. Booking confirmations are NOT auto-sent.",
              "بدون إعداد API. يرسل فريقك رسائل واتساب يدوياً عبر روابط wa.me. لا يتم إرسال تأكيدات الحجز تلقائياً.",
            )}
          </p>
        </button>

        <button
          onClick={() => setForm({ ...form, mode: "meta" })}
          data-testid="wa-mode-meta"
          className={`text-start p-5 rounded-xl border transition ${
            form.mode === "meta" ? "border-[var(--gold)] bg-[var(--gold)]/10 ring-2 ring-[var(--gold)]/30" : "border-[var(--border)] bg-[var(--surface)] hover:border-[var(--gold)]"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <KeyRound size={18} className="text-green-600" />
            <span className="font-heading text-lg">
              {t("Meta WhatsApp Cloud API", "Meta WhatsApp Cloud API")}
            </span>
          </div>
          <p className="text-xs text-[var(--text-muted)] leading-relaxed">
            {t(
              "Automatic booking confirmations via your own Meta credentials. Each tenant's settings are isolated.",
              "تأكيدات حجز آلية عبر بيانات Meta الخاصة بك. إعدادات كل مستأجر معزولة تماماً.",
            )}
          </p>
        </button>
      </div>

      {/* Meta credentials — visible only when mode=meta */}
      {isMeta && (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 space-y-4" data-testid="wa-meta-config">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-green-600" />
            <h3 className="font-heading text-lg">{t("Meta API Credentials", "بيانات اعتماد Meta")}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[var(--text-muted)] block mb-1">
                {t("Phone Number ID *", "معرّف رقم الهاتف *")}
              </label>
              <input data-testid="wa-meta-phone-id" className="input-dark"
                value={form.meta_phone_number_id}
                onChange={(e) => setForm({ ...form, meta_phone_number_id: e.target.value })}
                placeholder="e.g. 1234567890123" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] block mb-1">
                {t("API Version", "نسخة API")}
              </label>
              <input data-testid="wa-meta-api-ver" className="input-dark"
                value={form.meta_api_version}
                onChange={(e) => setForm({ ...form, meta_api_version: e.target.value })}
                placeholder="v18.0" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-[var(--text-muted)] block mb-1">
                {t("Access Token *", "رمز الوصول (Access Token) *")}
                {settings?.meta_access_token_set && (
                  <span className="ms-2 text-[var(--gold)]">
                    {t(`(saved: ${settings.meta_access_token_redacted})`, `(محفوظ: ${settings.meta_access_token_redacted})`)}
                  </span>
                )}
              </label>
              <input data-testid="wa-meta-token" className="input-dark font-mono text-xs" type="password"
                value={form.meta_access_token}
                onChange={(e) => setForm({ ...form, meta_access_token: e.target.value })}
                placeholder={settings?.meta_access_token_set
                  ? t("Leave empty to keep saved token", "اتركه فارغاً للإبقاء على المحفوظ")
                  : "EAAxxxxxxxxxxxxxxx"} />
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                {t("The token is stored server-side and never returned to the browser after saving.",
                   "يُحفظ الرمز على الخادم ولا يُعاد إلى المتصفح بعد الحفظ.")}
              </p>
            </div>
          </div>

          <div className="text-xs text-[var(--text-muted)] bg-[var(--gold)]/5 border border-[var(--border-subtle)] rounded p-3">
            {t("Get credentials from: ", "احصل على البيانات من: ")}
            <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-[var(--gold)] underline">
              developers.facebook.com
            </a>
            {" → WhatsApp → API Setup."}
          </div>
        </div>
      )}

      {/* Common controls */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 space-y-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <span>{t("Enabled (master switch)", "مفعّل (مفتاح رئيسي)")}</span>
        </label>
        <p className="text-[11px] text-[var(--text-muted)]">
          {t("When off, no WhatsApp messages are sent — even in Meta mode.",
             "عند الإيقاف، لا تُرسل أي رسائل واتساب — حتى في وضع Meta.")}
        </p>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="wa-save"
          className="btn-gold px-6 py-2 rounded-md flex items-center gap-2 disabled:opacity-50">
          <Save size={14} />
          {saving ? t("Saving...", "جارٍ الحفظ...") : t("Save settings", "حفظ الإعدادات")}
        </button>
      </div>

      {/* Status banner */}
      <div className={`flex items-center gap-2 p-3 rounded-md text-sm ${
        settings && (settings.mode === "meta" && metaConfigured)
          ? "bg-green-50 text-green-700 border border-green-200"
          : "bg-amber-50 text-amber-700 border border-amber-200"
      }`}>
        {settings && settings.mode === "meta" && metaConfigured
          ? <CheckCircle size={16}/>
          : <AlertCircle size={16}/>}
        <span>
          {settings && settings.mode === "meta" && metaConfigured
            ? t("Meta API mode — ready to send real messages.", "وضع Meta API — جاهز لإرسال رسائل حقيقية.")
            : settings && settings.mode === "stub"
              ? t("Standard mode — messages are logged only, not sent.", "الوضع العادي — الرسائل تُسجَّل ولا تُرسَل.")
              : t("Meta mode selected but credentials are incomplete.", "تم اختيار وضع Meta لكن البيانات ناقصة.")}
        </span>
      </div>

      {/* Send test */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 space-y-3">
        <h3 className="font-heading text-lg">{t("Send a test message", "إرسال رسالة اختبار")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input data-testid="wa-test-to" placeholder={t("Phone (e.g. 97455000000)", "رقم الجوال (مثال: 97455000000)")}
            className="input-dark" value={form.test_phone}
            onChange={(e) => setForm({ ...form, test_phone: e.target.value })} />
          <input data-testid="wa-test-body" placeholder={t("Message", "الرسالة")}
            className="input-dark md:col-span-2" value={testBody}
            onChange={(e) => setTestBody(e.target.value)} />
        </div>
        <button onClick={sendTest} disabled={sending} data-testid="wa-send-test"
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
          <Send size={14}/>
          {sending ? t("Sending...", "جارٍ الإرسال...") : t("Send test", "إرسال اختبار")}
        </button>
      </div>
    </div>
  );
}
